import React from 'react'
import styled from 'styled-components'
import { useTheme } from '../../../middleware/ThemeContext';
import { Theme } from '../../../utils/themes';
import StyledText from '../../StyledComponents/StyledText';
import { TempSettings } from '../../../types/types';
import { hexToRgba } from '../../../utils/helper';
import StyledInput from '../../StyledComponents/StyledInput';
import { StyledHorizontalLine } from '../../StyledComponents/Styledhr';
import { Radio } from '@mui/material';
import LeftAlignIcon from '../../../assets/icons/LeftAlignIcon';
import CenterAlignIcon from '../../../assets/icons/CenterAlignIcon';
import RightAlignIcon from '../../../assets/icons/RightAlignIcon';
import StyledRadio from '../../StyledComponents/StyledRadio';
import AMSSelect from '../../AMSSelect';
import { Form } from 'react-bootstrap';
import LeftRightLayoutIcon from '../../../assets/icons/LeftRightLayoutIcon';
import RightLeftLayoutIcon from '../../../assets/icons/RightLeftLayoutIcon';
import CenterStackLayoutIcon from '../../../assets/icons/CenterStackLayoutIcon';

const HeaderProperties = ({ updateTempSetting, tempSettings }: { updateTempSetting: (key: string, value: any) => void, tempSettings: TempSettings }) => {
    const { theme } = useTheme();
    const headingOptions = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
    const fontWeightOptions = ['Regular', 'Medium', 'Bold'];
    const textAlignmentOptions = [{ id: 'left-right', icon: <LeftRightLayoutIcon />, name: 'Left/Right' }, { id: "right-left", icon: <RightLeftLayoutIcon />, name: 'Right/Left' }, { id: "center-stacked", icon: <CenterStackLayoutIcon />, name: 'Center Stacked' }];
    const defaultOption = { value: 1, label: "Primary Menu (Header)" };

    return (
        <Container theme={theme}>
            <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Content</StyledText>
            <Section>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Select Menu</StyledText>
                <AMSSelect
                    showIcons
                    staticOptions={[
                        { value: 1, label: "Primary Menu (Header)" },
                        { value: 2, label: "About" },
                        { value: 3, label: "Contact" }
                    ]}
                    placeholder="Page Visibility"
                    value={defaultOption}   // ✅ always selected
                    isDisabled             // ✅ dropdown disabled
                    defaultOptions
                />
            </Section>



            <div className='mt-4 pt-2'>
                <StyledHorizontalLine theme={theme} />
            </div>

            <Section theme={theme}>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary} className='mb-4 mt-2'>Styling</StyledText>
                <div>
                    <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2'>Text Color</StyledText>
                    <Row>
                        <ColorInput
                            type="color"
                            value={tempSettings.textColor}
                            onChange={(e) => updateTempSetting('textColor', e.target.value)}
                        />

                        <HexInput
                            theme={theme}
                            type="text"
                            value={tempSettings.textColor}
                            onChange={(e) => updateTempSetting('textColor', e.target.value)}
                        />
                    </Row>
                </div>
                <div>
                    <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2'>Background Color</StyledText>

                    <Row>
                        <ColorInput
                            type="color"
                            value={tempSettings.backgroundColor}
                            onChange={(e) => updateTempSetting('backgroundColor', e.target.value)}
                        />

                        <HexInput
                            theme={theme}
                            type="text"
                            value={tempSettings.backgroundColor}
                            onChange={(e) => updateTempSetting('backgroundColor', e.target.value)}
                        />
                    </Row>
                </div>
            </Section>
            <div className='mt-2 pt-2'>
                <StyledHorizontalLine theme={theme} />
            </div>
            <Section theme={theme}>
                <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Layout</StyledText>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px', marginTop: '20px' }}>
                    {textAlignmentOptions.map((alignment) => {
                        const value = alignment.id.toLowerCase();

                        return (

                            <div key={value} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <StyledRadio borderWidth='2px' radioColor={theme.CorporateTheme.colors.primary} name="headerAlignment" checked={tempSettings.headerAlignment === value} onChange={() => updateTempSetting('headerAlignment', value)} label="" />
                                <AlignmentCard
                                    key={value}
                                    theme={theme}
                                    $isSelected={tempSettings.headerAlignment === value}
                                    onClick={() => updateTempSetting('headerAlignment', value)}
                                >
                                    {alignment.icon}
                                    <StyledText className='mt-2 mb-1' fontSize='10px' fontWeight={500} textColor={theme.CorporateTheme.colors.headerColor}>{alignment.name}</StyledText>
                                </AlignmentCard>
                            </div>
                        );
                    })}
                </div>
            </Section>

        </Container>
    )
}

const Container = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column; 
    border-radius: 8px;
    padding: 16px;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    `

const Section = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 26px;
`;


const OptionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const OptionButton = styled.button<{ theme: Theme, $isSelected?: boolean }>`
    flex: 1;
    padding: 8px 12px;
    border: 1px solid ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.border};
    background-color: ${({ theme, $isSelected }) =>
        $isSelected ? hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1) : theme.CorporateTheme.colors.background};
    color: ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.textPrimary};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
    
    /* Remove margin */
    margin: 0;
    
    /* Default no border-radius */
    border-radius: 0;
    
    /* First button - rounded left corners */
    &:first-child {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
    }

    /* Last button - rounded right corners */
    &:last-child {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
    }

    /* Remove double borders between buttons */
    &:not(:first-child) {
        margin-left: -1px;
    }

    /* IMPORTANT: Set z-index for all buttons to prevent overlap issues */
    position: relative;
    z-index: ${({ $isSelected }) => $isSelected ? 2 : 1};

    &:hover {
        background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        z-index: 3; /* Higher z-index on hover */
    }

    /* Selected state styling */
    ${({ $isSelected, theme }) => $isSelected && `
        background-color: ${hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${theme.CorporateTheme.colors.accentOrange};
        color: ${theme.CorporateTheme.colors.accentOrange};
        font-weight: 600;
        z-index: 2;
    `}
`;

const ColorBox = styled.div<{ color: string }>`
  width: 90px;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #dcdcdc;
  background: ${(p) => p.color};
`;

const HexInput = styled(Form.Control) <{ theme: Theme }>`
flex: 1;
  height: 42px;
    background: transparent;
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
`;

const ColorInput = styled.input`
  width: 105px;
  height: 42px;
  border-radius: 8px;
  border: 1px solid #dcdcdc;
  cursor: pointer;
  padding: 0;
`;


const Row = styled.div`
  display: flex;
    gap: 10px;
  align-items: center;
  margin-bottom: 20px;
`;

const TextInput = styled.input<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    &::placeholder {
        color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
    }
`;

const Select = styled.select<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    option {
        background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
        color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    }
`;


const AlignmentCard = styled.button<{
    theme: any;
    $isSelected: boolean;
}>`
flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
    border: 0.25px solid;
    border-image-source: linear-gradient(180deg, #0050FF 0%, rgba(0, 80, 255, 0) 100%);
    border-image-slice: 1;
  cursor: pointer;
    gap: 6px;
    background: linear-gradient(180deg, rgba(0, 80, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%);
    padding-top: 20px; 
  transition: all 0.2s ease;


`;



export default HeaderProperties
